/* eslint-disable max-len */
import { Col, message, Row } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import {
    type DealerWithPermissionsFragmentFragment,
    type MobilityModuleInDealerSpecsFragment,
} from '../../../../api/fragments';
import { useUpdateApplicationDealershipAssignmentsByDealerMutation } from '../../../../api/mutations/updateApplicationDealershipAssignmentsByDealer';
import { useUpdateMobilityEmailContentMutation } from '../../../../api/mutations/updateMobilityEmailContent';
import {
    ContentRefinementSourceAction,
    ContentRefinementSourceKind,
    type DealerBookingCodeSettingOverrideInput,
    EmailContentUpdateType,
    type TranslatedStringInput,
} from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import Form from '../../../../components/fields/Form';
import TranslatedMarkdownField from '../../../../components/fields/TranslatedMarkdownField';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../utilities/hasPermissions';
import useHandleError from '../../../../utilities/useHandleError';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useTooltip from '../../../../utilities/useTooltip';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import MobilityEmailScenarioContent from '../../ModuleDetailsPage/MobilityModulePage/MobilityEmailScenarioContent';
import { getMobilityEmailContentFromForm } from '../../ModuleDetailsPage/MobilityModulePage/shared';
import useHandleMobilityEmailAsset from '../../ModuleDetailsPage/MobilityModulePage/useHandleMobilityEmailAsset';

type DealerAssignmentMobilityFormProps = {
    module: MobilityModuleInDealerSpecsFragment;
    dealer: DealerWithPermissionsFragmentFragment;
};

type FormValues = {
    rentalDisclaimer: TranslatedStringInput;
    rentalRequirement: TranslatedStringInput;
    bookingCode: DealerBookingCodeSettingOverrideInput;
    emailContents: MobilityModuleInDealerSpecsFragment['mobilityEmailContents'];
};
const mobilityDealerValidations = validators.compose(
    validators.requiredBoolean('bookingCode.viewable'),
    validators.only(values => values.bookingCode?.viewable, validators.validMaxCharacter('bookingCode.value', true, 25))
);

const DealerAssignmentMobilityForm = ({ dealer, module }: DealerAssignmentMobilityFormProps) => {
    const { t } = useTranslation(['dealershipManagement', 'mobilityModuleDetails', 'moduleDetails']);

    const [mutation] = useUpdateApplicationDealershipAssignmentsByDealerMutation();
    const [updateEmailContentMutation] = useUpdateMobilityEmailContentMutation();

    const handleMobilityAsset = useHandleMobilityEmailAsset();

    const { yesNoSwitch } = useSystemSwitchData();

    const rentalDisclaimerToolTip = useTooltip('rentalDisclaimer');
    const rentalRequirementToolTip = useTooltip('rentalRequirement');

    // initialize finance products and dealers for the dealer
    const initialValues: FormValues = useMemo(
        () => ({
            rentalDisclaimer: module.rentalDisclaimer.overrides.find(({ dealerId }) => dealerId === dealer.id)
                ?.value ?? { defaultValue: '', overrides: [] },
            rentalRequirement: module.rentalRequirement.overrides.find(({ dealerId }) => dealerId === dealer.id)
                ?.value ?? { defaultValue: '', overrides: [] },
            bookingCode: module.bookingCode.overrides.find(({ dealerId }) => dealerId === dealer.id) ?? {
                viewable: false,
                value: '',
                dealerId: dealer.id,
            },
            emailContents: module.mobilityEmailContents,
        }),
        [
            dealer.id,
            module.bookingCode.overrides,
            module.mobilityEmailContents,
            module.rentalDisclaimer.overrides,
            module.rentalRequirement.overrides,
        ]
    );

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: hasPermissions(dealer.permissions, [permissionKind.updateDealer]),
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [dealer.permissions, module.id]
    );

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { rentalDisclaimer, rentalRequirement, bookingCode, emailContents } = values;

            // submitting message
            message.loading({
                content: t('dealershipManagement:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await mutation({
                variables: {
                    moduleId: module.id,

                    dealerRentalDisclaimer: {
                        dealerId: dealer.id,
                        value: rentalDisclaimer,
                    },
                    dealerRentalRequirement: {
                        dealerId: dealer.id,
                        value: rentalRequirement,
                    },

                    dealerBookingCode: {
                        ...bookingCode,
                        dealerId: dealer.id,
                    },
                },
            })
                .then(() =>
                    updateEmailContentMutation({
                        variables: {
                            emailContentUpdateType: EmailContentUpdateType.Dealer,
                            dealerId: dealer.id,
                            moduleId: module.id,
                            emailContents: getMobilityEmailContentFromForm({
                                emailContents,
                            }),
                        },
                    })
                )
                .finally(() => {
                    message.destroy('primary');
                });

            await handleMobilityAsset(module.id, values, EmailContentUpdateType.Dealer, initialValues, dealer.id);

            // show successful message
            message.success({
                content: t('dealershipManagement:messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [dealer.id, handleMobilityAsset, initialValues, module.id, mutation, t, updateEmailContentMutation]
    );
    const formId = `dealerForm-${module.id}`;

    const validate = useValidator(mobilityDealerValidations);
    const hasUpdatePermission = hasPermissions(dealer.permissions, [permissionKind.updateDealer]);

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <Formik<FormValues>
                initialValues={initialValues}
                onSubmit={onSubmit}
                validate={validate}
                enableReinitialize
            >
                {({ handleSubmit, values }) => (
                    <Form id={formId} name={formId} onSubmitCapture={handleSubmit}>
                        <CollapsibleWrapper defaultActiveKey={['configuration']}>
                            <Panel key="configuration" header={t('mobilityModuleDetails:subTitles.configuration')}>
                                <Row gutter={10}>
                                    <Col md={8} xs={24}>
                                        <TranslatedTextAreaField
                                            {...t('mobilityModuleDetails:fields.rentalDisclaimer', {
                                                returnObjects: true,
                                            })}
                                            disabled={!hasUpdatePermission}
                                            name="rentalDisclaimer"
                                            tooltip={rentalDisclaimerToolTip}
                                        />
                                    </Col>
                                    <Col md={8} xs={24}>
                                        <TranslatedMarkdownField
                                            {...t('mobilityModuleDetails:fields.rentalRequirement', {
                                                returnObjects: true,
                                            })}
                                            disabled={!hasUpdatePermission}
                                            name="rentalRequirement"
                                            tooltip={rentalRequirementToolTip}
                                        />
                                    </Col>
                                    <Col md={8} xs={24}>
                                        <FormFields.SwitchField
                                            {...t('mobilityModuleDetails:fields.bookingCodeViewable', {
                                                returnObjects: true,
                                            })}
                                            disabled={!hasUpdatePermission}
                                            name="bookingCode.viewable"
                                            {...yesNoSwitch}
                                        />
                                    </Col>
                                    {values.bookingCode.viewable && (
                                        <Col md={8} xs={24}>
                                            <FormFields.InputField
                                                {...t('mobilityModuleDetails:fields.bookingCodeText', {
                                                    returnObjects: true,
                                                })}
                                                disabled={!hasUpdatePermission}
                                                name="bookingCode.value"
                                                required
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </Panel>
                        </CollapsibleWrapper>

                        <CollapsibleWrapper defaultActiveKey={['email']}>
                            <Panel key="email" header={t('mobilityModuleDetails:tabs.emailContent.title')}>
                                <MobilityEmailScenarioContent
                                    disabled={!hasUpdatePermission}
                                    emailContents={module.mobilityEmailContents}
                                    isHomeDeliveryEnabled={module.homeDelivery.isEnable}
                                    locations={module.locations}
                                    sendEmailToCustomer={module.sendEmailToCustomer}
                                    targetDealerId={dealer.id}
                                    defaultValueDisabled
                                />
                            </Panel>
                        </CollapsibleWrapper>
                    </Form>
                )}
            </Formik>
        </GenericStateOnPageContextProvider>
    );
};

export default DealerAssignmentMobilityForm;
